import React, { useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import CustomDataGrid from "../Shared/Table/CustomDataGrid";
import { useGetReportPreviewQuery } from "../../store/api/devices/deviceApiSlice";
import { IReportsResponse } from "../../store/api/devices/types";
import { convertSymbols, errorMessage } from "../../utils/helpers";
import { useStoreDispatch } from "../../store/hooks";
import { setOpenToast } from "../../store/helpers/helperReducers";
import DownloadReport from "../../pages/Nodes/Reports/DownloadReport";
import { Sheet } from "../ui/sheet";
import { Icon } from "../Shared/Icon";
import { reportPreviewPriorityColumns, createReportPreviewColumn } from "../Shared/Table/Columns";
import { useSelector } from "react-redux";
import { paginationModelSelector } from "../../store/helpers/helperSelectors";
import { initialState } from "../../store/helpers/helperInitialState";
import { setPaginationModel } from "../../store/helpers/helperReducers";

interface ReportPreviewDrawerProps {
  open: boolean;
  onClose: () => void;
  report: IReportsResponse | null;
}

const TABLE_ID = "reportPreviewDrawer";

const ReportPreviewDrawer: React.FC<ReportPreviewDrawerProps> = ({ open, onClose, report }) => {
  const dispatch = useStoreDispatch();
  const paginationModel = useSelector(paginationModelSelector);
  const currentPageSize = paginationModel[TABLE_ID]?.pageSize ?? initialState.pagination.pageSize;

  useEffect(() => {
    if (!open || !report?.id) {
      return;
    }
    dispatch(setPaginationModel({ [TABLE_ID]: { page: 0, pageSize: currentPageSize } }));
  }, [dispatch, open, report?.id, currentPageSize]);

  const currentPagination = paginationModel[TABLE_ID] ?? initialState.pagination;

  const {
    data: previewData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetReportPreviewQuery(
    {
      id: report?.id ?? 0,
      page: currentPagination.page,
      size: currentPagination.pageSize,
    },
    {
      skip: !open || !report?.id,
    }
  );

  useEffect(() => {
    if (isError && error) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  }, [dispatch, error, isError]);

  const headers = previewData?.headers ?? [];
  const rows = previewData?.rows ?? [];
  const totalRows = previewData?.totalRows ?? 0;
  const isPreviewLoading = isLoading || isFetching;

  const reportTitle = convertSymbols(report?.reportName || "Report Preview");

  const columns = useMemo(() => {
    const normalizedHeadersMap = new Map(headers.map((header) => [header.trim().toLowerCase(), header]));

    const orderedHeaders = reportPreviewPriorityColumns
      .filter((col) => normalizedHeadersMap.has(col.name))
      .map((col) => ({ header: normalizedHeadersMap.get(col.name) as string, width: col.width }));

    const remainingHeaders = headers
      .filter((header) => !reportPreviewPriorityColumns.some((col) => col.name === header.trim().toLowerCase()))
      .map((header) => ({ header, width: 150 }));

    return [...orderedHeaders, ...remainingHeaders].map(({ header, width }) => createReportPreviewColumn(header, width));
  }, [headers]);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      width="clamp(900px, 70vw, 100vw)"
      useCardStyle={false}
      showCloseButton={false}
    >
      {/* Raise MUI portaled dropdowns (pagination rows-per-page) above Sheet overlay */}
      <style>{`.MuiPopover-root, .MuiMenu-root { z-index: 1400 !important; }`}</style>
      {/* Custom header: title/subtitle on left, close + download stacked on right */}
      <div className="flex items-start justify-between border-b border-border pb-4 mb-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold text-foreground m-0" title={reportTitle}>
            {reportTitle}
          </h2>
          {report?.type && (
            <p className="text-sm text-muted-foreground m-0">{report.type}</p>
          )}
        </div>
        <div className="flex flex-col items-center gap-2 shrink-0 ml-4">
          <button
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-md bg-transparent hover:bg-transparent text-foreground border-0 p-0 focus-visible:outline-none cursor-pointer"
          >
            <Icon name="close" size={20} />
          </button>
          <Box sx={{ pointerEvents: !report?.id || isPreviewLoading ? "none" : "auto", opacity: !report?.id || isPreviewLoading ? 0.6 : 1 }}>
            <DownloadReport rowData={report!} />
          </Box>
        </div>
      </div>

      <Box sx={{ height: "100%", minHeight: 0 }}>
        <CustomDataGrid
          tableId={TABLE_ID}
          rows={rows}
          columns={columns}
          rowCount={totalRows}
          loading={isPreviewLoading}
          disableColumnMenu={true}
          paginationMode="server"
          sortingMode="client"
          getRowId={(row) => row.__rowId}
          sx={{ height: "100%" }}
        />
      </Box>
    </Sheet>
  );
};

export default ReportPreviewDrawer;
